import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { supabase } from "../supabaseClient";
import { useAuth } from "./AuthContext";
import { useSeason } from "./SeasonContext";

const AdminContext = createContext(null);

export const useAdmin = () => {
  const ctx = useContext(AdminContext);
  if (!ctx) throw new Error("useAdmin must be used within AdminProvider");
  return ctx;
};

export const AdminProvider = ({ children }) => {
  const { user } = useAuth();
  // `selectedSeason` is "whichever season the admin dashboard is currently
  // viewing" — defaults to the live gameplay season but can be switched to
  // any season (e.g. the open registration season, or an archived one).
  const { seasons, selectedSeason, setSelectedSeason } = useSeason();

  const adminId = user?.id ?? null;

  const [teams, setTeams] = useState([]);
  const [captains, setCaptains] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!selectedSeason) return;
    setLoading(true);

    const [{ data: teamsData }, { data: captainRolesData }] = await Promise.all(
      [
        supabase
          .from("teams")
          .select("*")
          .eq("season_id", selectedSeason.id)
          .is("archived_at", null)
          .order("name"),
        // Query captains via profile_roles junction table, scoped to this season
        supabase
          .from("profile_roles")
          .select(
            "profile_id, team_id, season_id, profile:profiles(id, name, email, avatar_url), team:teams(id, name, logo_url, season_id)",
          )
          .eq("role", "team_captain")
          .eq("season_id", selectedSeason.id),
      ],
    );

    setTeams(teamsData || []);

    // Reshape captain role entries into the shape the rest of the UI expects:
    // { id, name, email, avatar_url, team: { id, name, logo_url, season_id } }
    const captains = (captainRolesData || []).map((entry) => ({
      ...(entry.profile || {}),
      id: entry.profile_id,
      team: entry.team || null,
    }));
    setCaptains(captains);
    setLoading(false);
  }, [selectedSeason]);

  // Re-fetch whenever the admin switches which season they're viewing
  useEffect(() => {
    loadData();
  }, [loadData]);

  const value = {
    adminId,
    activeSeason: selectedSeason, // the season the dashboard is currently viewing
    seasons, // full season list, for the season switcher
    setActiveSeason: setSelectedSeason,
    teams,
    setTeams, // allow panels to optimistically update (e.g. rename, logo)
    captains,
    loading,
    loadData,
  };

  return (
    <AdminContext.Provider value={value}>{children}</AdminContext.Provider>
  );
};
